import React, { useState } from 'react'
import Card from 'core-system/Card'
import {
  Database,
  Plug,
  Mail,
  MessageSquare,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  FileIcon,
  Zap,
  Shield,
  Clock,
  Home,
  Users,
  ChevronRight,
} from 'lucide-react'
import styled, { css } from 'styled-components'

type SectionKey =
  | 'overview'
  | 'knowledge'
  | 'programs'
  | 'channels'
  | 'behavior'

const MainContent = styled.main`
  flex: 1;
  overflow: auto;
`

const ContentContainer = styled.div`
  padding: 2rem;
`

const Layout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`

const Sidebar = styled.aside`
  width: 260px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem;
  position: sticky;
  top: 1.5rem;
`

const SidebarTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.75rem 0;
`

const NavButton = styled.button<{ $active: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  transition: all 0.2s;

  ${(props) =>
    props.$active &&
    css`
      background: #a64d79;
      color: #fff;
    `}

  &:hover {
    background: ${(props) => (props.$active ? '#a64d79' : '#f3f4f6')};
  }
`

const PageTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
`

const PageDescription = styled.p`
  margin: 0 0 1.5rem 0;
  color: #4b5563;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
`

const Grid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
`

const Metric = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Label = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`

const Value = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`

const Button = styled.button`
  border: none;
  background: #a64d79;
  color: #fff;
  font-size: 0.875rem;
  border-radius: 0.5rem;
  padding: 0.5rem 0.875rem;
  cursor: pointer;

  &:hover {
    background: #8b3d63;
  }
`

const LightButton = styled.button`
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #111827;
  font-size: 0.75rem;
  border-radius: 0.5rem;
  padding: 0.375rem 0.625rem;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
`

const Pill = styled.span<{ $type?: 'success' | 'warning' | 'inactive' }>`
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  border-radius: 999px;
  font-weight: 500;
  ${(props) =>
    props.$type === 'warning'
      ? css`
          background: #fef3c7;
          color: #b45309;
        `
      : props.$type === 'inactive'
        ? css`
            background: #f3f4f6;
            color: #4b5563;
          `
        : css`
            background: #dcfce7;
            color: #15803d;
          `}
`

const SourceRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
`

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: #a64d79;
`

const Field = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
`

const Select = styled.select`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
`

const Configuration = React.memo(() => {
  const [selectedSection, setSelectedSection] = useState<SectionKey>('overview')
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [autoSendEnabled, setAutoSendEnabled] = useState(true)
  const [slackEnabled, setSlackEnabled] = useState(true)
  const [includeFAQLinks, setIncludeFAQLinks] = useState(true)
  const [liveRAG, setLiveRAG] = useState(true)
  const [responseTone, setResponseTone] = useState('professional')
  const [verbosity, setVerbosity] = useState('detailed')
  const [maxTokens, setMaxTokens] = useState(350)

  const knowledgeSources = [
    {
      name: 'Commuter FAQ Sheet',
      type: 'Google Sheet',
      lastSync: 'Oct 20',
      status: 'active',
    },
    {
      name: 'NYC Commuter Ordinance PDF',
      type: 'Document',
      lastSync: 'Oct 15',
      status: 'active',
    },
    {
      name: 'Employee Benefits Portal',
      type: 'Web link',
      lastSync: 'Oct 22',
      status: 'warning',
    },
  ]

  const connectedPrograms = [
    {
      name: 'Transit Subsidy',
      status: 'active',
      access: 'Full',
      description: 'Can answer eligibility, balances, renewal, card delivery',
      lastUpdate: '2 days ago',
    },
    {
      name: 'Parking Benefits',
      status: 'active',
      access: 'Policy + Admin',
      description: 'Connected to ParkingVendorAPI',
      lastUpdate: '1 day ago',
    },
    {
      name: 'Micromobility',
      status: 'active',
      access: 'FAQ + Support',
      description: 'Employee usage analytics enabled',
      lastUpdate: '3 days ago',
    },
    {
      name: 'Shuttles / Employer Transport',
      status: 'inactive',
      access: 'Not configured',
      description: 'Integration pending',
      lastUpdate: 'N/A',
    },
  ]

  const dataSources = [
    { name: 'HRIS (ADP, Gusto)', icon: Users },
    { name: 'Payroll', icon: FileIcon },
    { name: 'Transit Vendor API (Masabi)', icon: Zap },
    { name: 'Fleet Program Directory', icon: Database },
  ]

  return (
    <MainContent>
      <ContentContainer>
        <Layout>
          <Sidebar>
            <SidebarTitle>Configuration</SidebarTitle>
            <NavButton
              $active={selectedSection === 'overview'}
              onClick={() => setSelectedSection('overview')}
            >
              <Home style={{ width: '1rem', height: '1rem' }} /> Overview
            </NavButton>
            <NavButton
              $active={selectedSection === 'knowledge'}
              onClick={() => setSelectedSection('knowledge')}
            >
              <Database style={{ width: '1rem', height: '1rem' }} /> Knowledge &
              RAG Sources
            </NavButton>
            <NavButton
              $active={selectedSection === 'programs'}
              onClick={() => setSelectedSection('programs')}
            >
              <Plug style={{ width: '1rem', height: '1rem' }} /> Connected
              Programs
            </NavButton>
            <NavButton
              $active={selectedSection === 'channels'}
              onClick={() => setSelectedSection('channels')}
            >
              <MessageSquare style={{ width: '1rem', height: '1rem' }} />{' '}
              Channels & Automation
            </NavButton>
            <NavButton
              $active={selectedSection === 'behavior'}
              onClick={() => setSelectedSection('behavior')}
            >
              <Sparkles style={{ width: '1rem', height: '1rem' }} /> Model
              Behavior
            </NavButton>
          </Sidebar>

          <div style={{ flex: 1 }}>
            {selectedSection === 'overview' && (
              <Section>
                <div>
                  <PageTitle>Configuration Overview</PageTitle>
                  <PageDescription>
                    A snapshot of your Fleet AI Concierge setup, knowledge
                    sources, and connected systems.
                  </PageDescription>
                </div>

                <Card style={{ padding: '1.5rem' }}>
                  <h3>System Metrics</h3>
                  <Grid2>
                    <Metric>
                      <div>
                        <Label>FAQs Linked</Label>
                        <Value>128</Value>
                      </div>
                      <Database color='#A64D79' />
                    </Metric>
                    <Metric>
                      <div>
                        <Label>Documents Indexed</Label>
                        <Value>34</Value>
                      </div>
                      <FileIcon color='#A64D79' />
                    </Metric>
                    <Metric>
                      <div>
                        <Label>Programs Recognized</Label>
                        <Value>12</Value>
                      </div>
                      <Plug color='#A64D79' />
                    </Metric>
                    <Metric>
                      <div>
                        <Label>Slack Bot Status</Label>
                        <div style={{ color: '#16a34a', fontWeight: 600 }}>
                          Active
                        </div>
                      </div>
                      <MessageSquare color='#A64D79' />
                    </Metric>
                  </Grid2>
                </Card>

                <Card style={{ padding: '1.5rem' }}>
                  <Row>
                    <h3 style={{ margin: 0 }}>Model Configuration</h3>
                    <Button>
                      <RefreshCw
                        style={{
                          width: '0.875rem',
                          height: '0.875rem',
                          marginRight: '0.35rem',
                        }}
                      />
                      Refresh Model
                    </Button>
                  </Row>
                  <div
                    style={{
                      background: '#f9fafb',
                      borderRadius: '0.5rem',
                      marginTop: '1rem',
                      padding: '1rem',
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <Sparkles color='#A64D79' style={{ width: '1rem' }} />
                      <strong>GPT-4 Turbo (Fleet custom fine-tune)</strong>
                    </div>
                  </div>
                </Card>

                <Card style={{ padding: '1.5rem' }}>
                  <h3>Connected Data Sources</h3>
                  <Grid4>
                    {dataSources.map((source) => {
                      const Icon = source.icon
                      return (
                        <div
                          key={source.name}
                          style={{
                            textAlign: 'center',
                            padding: '0.75rem',
                            background: '#f9fafb',
                            borderRadius: '0.5rem',
                          }}
                        >
                          <Icon
                            color='#A64D79'
                            style={{ marginBottom: '0.35rem' }}
                          />
                          <div
                            style={{ fontSize: '0.75rem', color: '#374151' }}
                          >
                            {source.name}
                          </div>
                        </div>
                      )
                    })}
                  </Grid4>
                </Card>
              </Section>
            )}

            {selectedSection === 'knowledge' && (
              <Section>
                <div>
                  <PageTitle>Knowledge & RAG Sources</PageTitle>
                  <PageDescription>
                    Manage how the AI Concierge is trained and how its retrieval
                    system works.
                  </PageDescription>
                </div>
                <Card style={{ padding: '1.5rem' }}>
                  <Row>
                    <h3 style={{ margin: 0 }}>Knowledge Source List</h3>
                    <Button>+ Add Source</Button>
                  </Row>
                  <table style={{ width: '100%', marginTop: '1rem' }}>
                    <thead>
                      <tr>
                        <th align='left'>Source</th>
                        <th align='left'>Type</th>
                        <th align='left'>Last Sync</th>
                        <th align='left'>Status</th>
                        <th align='left'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {knowledgeSources.map((source) => (
                        <SourceRow key={source.name}>
                          <td style={{ padding: '0.75rem 0' }}>
                            {source.name}
                          </td>
                          <td>{source.type}</td>
                          <td>{source.lastSync}</td>
                          <td>
                            <Pill
                              $type={
                                source.status === 'warning'
                                  ? 'warning'
                                  : 'success'
                              }
                            >
                              {source.status === 'warning'
                                ? 'Warning'
                                : 'Active'}
                            </Pill>
                          </td>
                          <td>
                            <LightButton>Resync</LightButton>{' '}
                            <LightButton>Preview</LightButton>{' '}
                            <LightButton>Disable</LightButton>
                          </td>
                        </SourceRow>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </Section>
            )}

            {selectedSection === 'programs' && (
              <Section>
                <div>
                  <PageTitle>Connected Programs</PageTitle>
                  <PageDescription>
                    Display the commuter programs the Concierge has access to
                    and supports.
                  </PageDescription>
                </div>
                <Grid2>
                  {connectedPrograms.map((program) => (
                    <Card key={program.name} style={{ padding: '1.25rem' }}>
                      <Row>
                        <strong>{program.name}</strong>
                        <Pill
                          $type={
                            program.status === 'active' ? 'success' : 'inactive'
                          }
                        >
                          {program.status === 'active' ? 'Active' : 'Inactive'}
                        </Pill>
                      </Row>
                      <div style={{ marginTop: '0.75rem', color: '#4b5563' }}>
                        <div>
                          <Shield style={{ width: 14, marginRight: 6 }} />
                          Access level: {program.access}
                        </div>
                        <div>
                          <Clock style={{ width: 14, marginRight: 6 }} />
                          Last update: {program.lastUpdate}
                        </div>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                        {program.description}
                      </p>
                      {program.status === 'inactive' && (
                        <Button>Configure Integration</Button>
                      )}
                    </Card>
                  ))}
                </Grid2>
              </Section>
            )}

            {selectedSection === 'channels' && (
              <Section>
                <div>
                  <PageTitle>Channels & Automation</PageTitle>
                  <PageDescription>
                    Configure communication channels where Fleet AI Concierge
                    operates.
                  </PageDescription>
                </div>
                <Card style={{ padding: '1.5rem' }}>
                  <Row>
                    <div>
                      <h3 style={{ margin: 0 }}>Auto-Draft Email</h3>
                    </div>
                    <Toggle
                      checked={emailEnabled}
                      onChange={(e) => setEmailEnabled(e.target.checked)}
                    />
                  </Row>
                  {emailEnabled && (
                    <div style={{ marginTop: '1rem' }}>
                      <Label>Default sender alias</Label>
                      <Field value='commuting@company.com' readOnly />
                      <Row style={{ marginTop: '0.75rem' }}>
                        <span>Auto-send low-risk responses</span>
                        <Toggle
                          checked={autoSendEnabled}
                          onChange={(e) => setAutoSendEnabled(e.target.checked)}
                        />
                      </Row>
                    </div>
                  )}
                </Card>

                <Card style={{ padding: '1.5rem' }}>
                  <Row>
                    <h3 style={{ margin: 0 }}>Slack Bot Integration</h3>
                    <Toggle
                      checked={slackEnabled}
                      onChange={(e) => setSlackEnabled(e.target.checked)}
                    />
                  </Row>
                  {slackEnabled && (
                    <div style={{ marginTop: '1rem' }}>
                      <Label>Tone</Label>
                      <Select>
                        <option>Professional</option>
                        <option>Conversational</option>
                        <option>Friendly</option>
                      </Select>
                    </div>
                  )}
                </Card>
              </Section>
            )}

            {selectedSection === 'behavior' && (
              <Section>
                <div>
                  <PageTitle>Model Behavior</PageTitle>
                  <PageDescription>
                    Configure Concierge personality, tone, and response
                    constraints.
                  </PageDescription>
                </div>
                <Card style={{ padding: '1.5rem' }}>
                  <h3>Response Configuration</h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <Label>Response Tone</Label>
                    <Select
                      value={responseTone}
                      onChange={(e) => setResponseTone(e.target.value)}
                    >
                      <option value='professional'>Professional</option>
                      <option value='friendly'>Friendly</option>
                      <option value='compliance'>Compliance-first</option>
                    </Select>
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <Label>Verbosity Level</Label>
                    <Select
                      value={verbosity}
                      onChange={(e) => setVerbosity(e.target.value)}
                    >
                      <option value='concise'>Concise</option>
                      <option value='detailed'>Detailed</option>
                      <option value='context-rich'>Context-rich</option>
                    </Select>
                  </div>
                  <Label>Maximum token length per response</Label>
                  <Field
                    type='number'
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(Number(e.target.value))}
                  />
                </Card>

                <Card style={{ padding: '1.5rem' }}>
                  <h3>Advanced Settings</h3>
                  <Row>
                    <span>Include links to internal FAQs in replies</span>
                    <Toggle
                      checked={includeFAQLinks}
                      onChange={(e) => setIncludeFAQLinks(e.target.checked)}
                    />
                  </Row>
                  <Row>
                    <span>Use live RAG retrieval for each new question</span>
                    <Toggle
                      checked={liveRAG}
                      onChange={(e) => setLiveRAG(e.target.checked)}
                    />
                  </Row>
                </Card>
              </Section>
            )}
          </div>
        </Layout>
      </ContentContainer>
    </MainContent>
  )
})

if (process.env.NODE_ENV !== 'production') {
  Configuration.displayName = 'Configuration'
}

export default Configuration
